/**
 * LiveGenerationToaster — iter v28 r423 (User M1333)
 *
 * Toast globale che avvisa l'agente quando una foto generata su `/live/*`
 * (enhance / restyle / empty-room / stage) è pronta MENTRE lui ha cambiato
 * sezione dell'app.
 *
 * Pattern:
 *   - Polling `GET /api/live/history?limit=12` ogni 8 secondi
 *   - Set di "seen IDs" persisted in localStorage (sopravvive ai refresh)
 *   - Al primo mount: tutti gli ID correnti vengono marcati come "già visti"
 *     così non bombardiamo l'utente con vecchie notifiche
 *   - Se l'utente è su `/dashboard/foto*` → silent (FotoEditor.jsx già mostra
 *     lo stato batch in tempo reale, sarebbe duplicato fastidioso)
 *   - Toast con bottone "Vedi" → `/dashboard/contenuti`
 *
 * Montato globalmente in `AppShell.jsx` così resta attivo qualunque rotta
 * l'utente stia navigando dentro la dashboard.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

const POLL_INTERVAL_MS = 8000;
const STORAGE_KEY = "pronto:live-seen-ids";
const MAX_REMEMBERED = 200; // tetto del Set per non far esplodere localStorage

export default function LiveGenerationToaster() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const seenIdsRef = useRef(new Set());
  const initializedRef = useRef(false);
  const locationRef = useRef(location.pathname);

  // Tieni traccia della rotta corrente per il tick (closure stable).
  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (!user?.user_id) return;

    // Init from localStorage
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      seenIdsRef.current = new Set(Array.isArray(stored) ? stored : []);
    } catch {
      seenIdsRef.current = new Set();
    }

    let intervalId = null;
    let cancelled = false;

    const persist = () => {
      try {
        const arr = [...seenIdsRef.current].slice(-MAX_REMEMBERED);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
      } catch { /* ignore */ }
    };

    const tick = async () => {
      try {
        const { data } = await api.get("/live/history", { params: { limit: 12 } });
        if (cancelled) return;
        const items = Array.isArray(data) ? data : [];
        if (!initializedRef.current) {
          // Primo giro: marca tutto come visto (no toast retroattivi)
          items.forEach((it) => { if (it?.id) seenIdsRef.current.add(it.id); });
          initializedRef.current = true;
          persist();
          return;
        }
        const newOnes = items.filter((it) => it?.id && !seenIdsRef.current.has(it.id));
        newOnes.forEach((it) => seenIdsRef.current.add(it.id));
        if (newOnes.length === 0) return;

        // Skip toast se l'utente è già in FotoEditor (vede lo stato batch lì)
        const path = locationRef.current || "";
        const onFotoEditor = path.startsWith("/dashboard/foto");
        persist();
        if (onFotoEditor) return;

        // Costruisci messaggio in base al numero di nuovi
        const count = newOnes.length;
        const message = count === 1
          ? "Foto pronta · è nella tua galleria"
          : `${count} foto pronte · sono nella tua galleria`;

        toast.success(message, {
          duration: 12000,
          action: {
            label: "Vedi",
            onClick: () => navigate("/dashboard/contenuti"),
          },
        });
      } catch {
        // Silent: rete giù o 401 → riproviamo al prossimo tick
      }
    };

    // Lancia subito + ripeti ogni POLL_INTERVAL_MS
    tick();
    intervalId = setInterval(tick, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [user?.user_id, navigate]);

  return null;
}
